import { supabase } from '@/integrations/supabase/client';

/**
 * GDPR-safe whitelist of producer fields allowed in audit before/after snapshots.
 * No PII (email, phone, address, description, free-text) is ever stored.
 */
const SAFE_PRODUCER_KEYS = new Set([
  'status',
  'verified',
  'verified_at',
  'producer_status',
  'place_id',
  'categories',
  'slug',
  'published_at',
  'commerce_provider',
  'suspended_at',
  'verification_submitted_at',
]);

type ProducerSnapshot = Record<string, unknown>;

/** Swedish labels for changed keys used in summary generation */
const KEY_LABELS: Record<string, string> = {
  status: 'status',
  verified: 'verifierad',
  verified_at: 'verifieringsdatum',
  producer_status: 'producentstatus',
  place_id: 'plats',
  categories: 'kategorier',
  slug: 'slug',
  published_at: 'publiceringsdatum',
  commerce_provider: 'betalningsleverantör',
  suspended_at: 'avstängningsdatum',
};

const ACTION_LABELS: Record<string, string> = {
  update: 'Uppdaterade',
  publish: 'Publicerade',
  unpublish: 'Avpublicerade',
  verify: 'Verifierade',
  pause: 'Pausade',
  suspend: 'Stängde av',
  created: 'Skapade',
  delete: 'Tog bort',
};

function pickSafeKeys(obj: ProducerSnapshot): ProducerSnapshot {
  const safe: ProducerSnapshot = {};
  for (const key of Object.keys(obj)) {
    if (SAFE_PRODUCER_KEYS.has(key)) {
      safe[key] = obj[key];
    }
  }
  return safe;
}

function computeChangedKeys(
  before: ProducerSnapshot,
  after: ProducerSnapshot
): string[] {
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  const changed: string[] = [];
  for (const key of keys) {
    if (!SAFE_PRODUCER_KEYS.has(key)) continue;
    if (JSON.stringify(before[key]) !== JSON.stringify(after[key])) {
      changed.push(key);
    }
  }
  return changed.sort();
}

function buildSummary(action: string, changedKeys: string[]): string {
  const verb = ACTION_LABELS[action] || action;
  if (changedKeys.length === 0) return `${verb} producent`;
  const labels = changedKeys.map((k) => KEY_LABELS[k] || k);
  return `${verb}: ${labels.join(', ')}`;
}

export interface SafeAuditParams {
  entityId: string;
  action: string;
  before?: ProducerSnapshot;
  after?: ProducerSnapshot;
}

/**
 * Log a GDPR-safe admin audit event for a producer.
 * Only whitelisted fields are stored in before/after.
 */
export async function logAdminAuditSafe(params: SafeAuditParams): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const safeBefore = params.before ? pickSafeKeys(params.before) : null;
    const safeAfter = params.after ? pickSafeKeys(params.after) : null;
    const changedKeys = (params.before && params.after)
      ? computeChangedKeys(params.before, params.after)
      : [];
    const summary = buildSummary(params.action, changedKeys);

    const { error } = await supabase.from('admin_audit_log').insert({
      entity_type: 'producer',
      entity_id: params.entityId,
      action: params.action,
      actor_user_id: user.id,
      before: safeBefore as any,
      after: safeAfter as any,
      changed_keys: changedKeys,
      pii_redacted: true,
      summary,
    });

    if (error) {
      console.error('[logAdminAuditSafe] insert failed:', error.message);
    }
  } catch (err) {
    console.error('[logAdminAuditSafe] exception:', err);
  }
}
